import axios from 'axios';

export const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});

// Add token to every request from localStorage
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ─────────────────────────────────────────────────────────────
//  Silent token refresh on 401.
//  Flow: any 401 (except auth endpoints) → try refresh once →
//  on success retry the original request invisibly. On failure,
//  toast the user, then redirect to login. No more "I clicked
//  resize and got logged out" surprise UX.
// ─────────────────────────────────────────────────────────────
let refreshPromise = null;  // de-dupes concurrent refresh attempts
let lastSessionExpiredToastAt = 0;

async function tryRefreshAccessToken() {
  // Single in-flight refresh promise — if multiple requests 401 simultaneously
  // they all await the same refresh call, then retry.
  if (refreshPromise) return refreshPromise;
  const refresh = localStorage.getItem('refresh_token');
  if (!refresh) return Promise.resolve(null);

  refreshPromise = (async () => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/refresh`,
        { refresh_token: refresh },
        { headers: { 'Content-Type': 'application/json' }, withCredentials: true },
      );
      const newToken = data?.access_token;
      if (newToken) {
        localStorage.setItem('access_token', newToken);
        return newToken;
      }
      return null;
    } catch {
      return null;
    } finally {
      // Allow a future refresh after this one resolves (success or fail)
      setTimeout(() => { refreshPromise = null; }, 200);
    }
  })();
  return refreshPromise;
}

function showSessionExpiredToast() {
  // Throttle: don't fire more than once per 5 seconds (multiple 401s would otherwise stack)
  const now = Date.now();
  if (now - lastSessionExpiredToastAt < 5000) return;
  lastSessionExpiredToastAt = now;
  // Use sonner if it's loaded; fall back to a quiet console log otherwise.
  try {
    // Dynamically import to avoid coupling — sonner is already in the bundle via the toast system.
    import('sonner').then(({ toast }) => {
      toast.error('Session expired — please sign in again.', {
        duration: 4000,
        position: 'top-center',
      });
    }).catch(() => {});
  } catch {}
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const url = error.config?.url || '';
    const isAuthCall = url.includes('/auth/');

    // Only handle silent-refresh on 401 from non-auth endpoints, and only retry once
    if (status === 401 && !isAuthCall && !error.config.__isRetry) {
      const newToken = await tryRefreshAccessToken();
      if (newToken) {
        // Mutate the original config and retry. New token will be attached
        // by the request interceptor automatically (it reads localStorage).
        error.config.__isRetry = true;
        return api.request(error.config);
      }
      // Refresh failed (no refresh token, or refresh token expired)
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      showSessionExpiredToast();
      // Soft redirect with a small delay so the toast is visible
      setTimeout(() => {
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
      }, 1200);
    }
    return Promise.reject(error);
  }
);

// ─────────────────────────────────────────────────────────────
//  BULLETPROOF CACHE RESET — runs on every app boot
//  Ensures users never get stuck on stale JS after a deploy.
// ─────────────────────────────────────────────────────────────
async function hardPurgeCaches() {
  try {
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map(n => caches.delete(n)));
    }
  } catch {}
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map(r => r.unregister()));
    }
  } catch {}
}

let pendingVersionReload = null;

function reloadForVersion(version) {
  const url = new URL(window.location.href);
  url.searchParams.set('_v', version);
  window.location.replace(url.toString());
}

async function checkVersion({ deferIfVisible = false } = {}) {
  try {
    // Bust any CDN caching on the version endpoint
    const { data } = await axios.get(`${API_URL}/api/version?_t=${Date.now()}`, {
      headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
    });
    const storedVersion = localStorage.getItem('app_version');
    if (storedVersion && storedVersion !== data.version) {
      // App was updated — purge all caches AND reload with cache-bust query
      localStorage.setItem('app_version', data.version);
      await hardPurgeCaches();
      if (deferIfVisible && !document.hidden) {
        // Don't yank the page out from under an active user — reload as soon
        // as the tab goes to the background instead.
        pendingVersionReload = data.version;
        return;
      }
      reloadForVersion(data.version);
      return;
    }
    localStorage.setItem('app_version', data.version);
  } catch {
    // Ignore version check failures
  }
}

// Defensive: always unregister any old service workers on boot
// (prevents "old HTML points to dead JS" deadlock)
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then(regs => {
    regs.forEach(r => r.unregister());
  }).catch(() => {});
}

// Check version on load
checkVersion();

// Long-lived tabs are the #1 cause of "stale JS after a deploy" bugs.
// Re-check every 10 minutes; if a new version shipped, reload invisibly
// the moment the tab goes to the background (never mid-typing).
setInterval(() => checkVersion({ deferIfVisible: true }), 10 * 60 * 1000);
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    if (pendingVersionReload) reloadForVersion(pendingVersionReload);
    else checkVersion();
  }
});

// Public helper: force an immediate version check (no defer). Used by
// Crunchy on open so long-lived tabs can't run AI flows on a stale bundle.
export async function forceVersionCheck() {
  return checkVersion();
}

// ─────────────────────────────────────────────────────────────
//  KEEPALIVE — refresh the access token in the background every
//  25 minutes for active users. Prevents the "session expired
//  mid-typing in Crunchy" paper cut. Uses /auth/me (lightweight)
//  which goes through the axios interceptor's silent-refresh logic.
//  Skipped if no token (user not logged in) or tab hidden.
// ─────────────────────────────────────────────────────────────
const KEEPALIVE_MS = 25 * 60 * 1000;
setInterval(() => {
  if (document.hidden) return;
  if (!localStorage.getItem('access_token')) return;
  api.get('/auth/me').catch(() => {}); // 401 → interceptor refreshes silently
}, KEEPALIVE_MS);

// ─────────────────────────────────────────────────────────────
//  Public helper for non-axios fetch paths (Crunchy SSE etc.)
//  to ensure the access token in localStorage is fresh before
//  firing a raw fetch() request.
// ─────────────────────────────────────────────────────────────
export async function ensureFreshToken() {
  if (!localStorage.getItem('access_token')) return null;
  try {
    await api.get('/auth/me'); // 401 → interceptor refreshes; returns new token
  } catch {
    // refresh failed → interceptor already redirected; nothing we can do
  }
  return localStorage.getItem('access_token');
}

export default api;
